package kz.dentalcrm.app.core.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronDown
import compose.icons.feathericons.ChevronUp
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.DrawableResource

@OptIn(ExperimentalComposeUiApi::class)
@Stable
@Composable
fun <T> UISelfDropDownTextInput(
    modifier: Modifier,
    enabled: Boolean = true,
    borderWidth: Dp = 1.dp,

    isOnError: Boolean = false,
    errorText: String = "",
    errorIconId: DrawableResource? = null,

    leadingIcon: ImageVector? = null,
    leadingIconSize: Dp = 16.dp,

    label: String = "",
    hint: String = "",
    hintIconId: DrawableResource? = null,
    value: String = "",
    cornerShape: Dp = 16.dp,
    backgroundColor: Color = DefaultTheme.colors.surfaceInput,

    // Dropdown specific
    items: List<T> = emptyList(),
    selectedItems: List<T> = emptyList(),
    itemToString: (T) -> String = { it.toString() },
    multiSelect: Boolean = true,
    clearOnSelect: Boolean = false,
    onItemsSelected: (List<T>) -> Unit = {},

    additionalPaddingValues: PaddingValues = PaddingValues(vertical = 4.dp, horizontal = 8.dp),
) {
    val focusManager = LocalFocusManager.current
    val keyboardController = LocalSoftwareKeyboardController.current
    val focusRequester = remember { FocusRequester() }

    var searchText by remember { mutableStateOf(value) }
    var isFocused by remember { mutableStateOf(false) }
    var isExpanded by remember { mutableStateOf(false) }

    // Sync searchText with external value when it changes (for controlled input scenarios)
    LaunchedEffect(value) {
        if (!isFocused && value != searchText) {
            searchText = value
        }
    }

    val filteredItems = remember(searchText, items) {
        if (searchText.isEmpty()) {
            items
        } else {
            items.filter { itemToString(it).contains(searchText, ignoreCase = true) }
        }
    }

    val labelStyle = when {
        isFocused -> DefaultTheme.typography.labelRegular
        searchText.isNotEmpty() -> DefaultTheme.typography.labelRegular
        else -> DefaultTheme.typography.bodyMediumMedium
    }
    val borderColor = when {
        isOnError -> DefaultTheme.colors.error
        isFocused -> DefaultTheme.colors.primary
        else -> DefaultTheme.colors.outline
    }

    fun closeDropdown() {
        isExpanded = false
        isFocused = false
        keyboardController?.hide()
        focusManager.clearFocus()
    }

    Column {
        // TextField with clickable trailing icon
        TextField(
            modifier = modifier
                .focusRequester(focusRequester)
                .background(
                    color = backgroundColor,
                    shape = RoundedCornerShape(cornerShape)
                )
                .border(
                    width = borderWidth,
                    color = borderColor,
                    shape = RoundedCornerShape(cornerShape)
                )
                .onFocusChanged { focusState ->
                    isFocused = focusState.isFocused
                    if (focusState.isFocused) {
                        isExpanded = true
                    }
                },
            colors = TextFieldDefaults.colors(
                focusedContainerColor = Color.Transparent,
                unfocusedContainerColor = Color.Transparent,
                disabledContainerColor = Color.Transparent,
                errorContainerColor = Color.Transparent,
                errorLeadingIconColor = Color.Transparent,
                errorTrailingIconColor = Color.Transparent,
                errorIndicatorColor = Color.Transparent,
                disabledIndicatorColor = Color.Transparent,
                disabledLeadingIconColor = Color.Transparent,
                disabledTrailingIconColor = Color.Transparent,
                focusedIndicatorColor = Color.Transparent,
                unfocusedIndicatorColor = Color.Transparent
            ),
            shape = RoundedCornerShape(cornerShape),
            textStyle = DefaultTheme.typography.bodyMediumMedium.copy(
                color = DefaultTheme.colors.onSurface,
            ),
            leadingIcon = leadingIcon?.let {
                {
                    Icon(
                        imageVector = leadingIcon,
                        contentDescription = null,
                        tint = borderColor,
                        modifier = Modifier.size(leadingIconSize)
                    )
                }
            },
            label = if (label.isNotEmpty()) {
                {
                    Text(
                        text = label,
                        style = labelStyle,
                        color = borderColor
                    )
                }
            } else null,
            trailingIcon = {
                Icon(
                    imageVector = if (isExpanded) FeatherIcons.ChevronUp else FeatherIcons.ChevronDown,
                    contentDescription = null,
                    tint = borderColor,
                    modifier = Modifier
                        .size(16.dp)
                        .clickable(
                            indication = null,
                            interactionSource = remember { MutableInteractionSource() }
                        ) {
                            if (isExpanded) {
                                closeDropdown()
                            } else {
                                isExpanded = true
                                focusRequester.requestFocus()
                            }
                        }
                )
            },
            enabled = enabled,
            value = searchText,
            maxLines = 1,
            singleLine = true,
            keyboardOptions = KeyboardOptions(
                imeAction = ImeAction.Done,
                keyboardType = KeyboardType.Text
            ),
            keyboardActions = KeyboardActions(
                onDone = {
                    closeDropdown()
                }
            ),
            onValueChange = { newValue ->
                searchText = newValue
                isExpanded = true
            },
        )

        // Inline dropdown list (no popup - works better on iOS)
        AnimatedVisibility(
            visible = isExpanded && filteredItems.isNotEmpty(),
            enter = expandVertically(),
            exit = shrinkVertically()
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 4.dp)
                    .heightIn(max = 200.dp)
                    .background(
                        color = DefaultTheme.colors.surfaceBackground,
                        shape = RoundedCornerShape(12.dp)
                    )
                    .border(
                        width = 1.dp,
                        color = DefaultTheme.colors.outline,
                        shape = RoundedCornerShape(12.dp)
                    )
                    .verticalScroll(rememberScrollState())
            ) {
                Spacer(modifier = Modifier.height(8.dp))

                // "Select All" option for multi-select
                if (multiSelect) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 8.dp, vertical = 4.dp)
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null
                            ) {
                                if (selectedItems.size == filteredItems.size) {
                                    onItemsSelected(emptyList())
                                } else {
                                    onItemsSelected(filteredItems)
                                }
                            }
                            .padding(horizontal = 8.dp, vertical = 6.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Checkbox(
                            checked = selectedItems.size == filteredItems.size && filteredItems.isNotEmpty(),
                            onCheckedChange = null,
                            colors = CheckboxDefaults.colors(
                                checkedColor = DefaultTheme.colors.primary,
                                uncheckedColor = DefaultTheme.colors.outline
                            )
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            text = "Выбрать все",
                            style = DefaultTheme.typography.bodyMediumMedium,
                            color = DefaultTheme.colors.onSurface
                        )
                    }
                }

                filteredItems.forEach { item ->
                    val isSelected = selectedItems.contains(item)

                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null
                            ) {
                                if (multiSelect) {
                                    val newSelection = if (isSelected) {
                                        selectedItems - item
                                    } else {
                                        selectedItems + item
                                    }
                                    onItemsSelected(newSelection)
                                } else {
                                    onItemsSelected(listOf(item))
                                    searchText = if (clearOnSelect) "" else itemToString(item)
                                    closeDropdown()
                                }
                            }
                            .padding(horizontal = 8.dp, vertical = 4.dp)
                            .background(
                                color = if (isSelected) DefaultTheme.colors.primarySubdued else Color.Transparent,
                                shape = RoundedCornerShape(8.dp)
                            )
                            .padding(
                                horizontal = 8.dp,
                                vertical = 8.dp
                            ),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (multiSelect) {
                            Checkbox(
                                checked = isSelected,
                                onCheckedChange = null,
                                colors = CheckboxDefaults.colors(
                                    checkedColor = DefaultTheme.colors.primary,
                                    uncheckedColor = DefaultTheme.colors.outline
                                )
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                        }
                        Text(
                            text = itemToString(item),
                            style = DefaultTheme.typography.bodyMediumRegular,
                            color = DefaultTheme.colors.onSurface
                        )
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
            }
        }

        AnimatedVisibility(visible = hint.isNotEmpty()) {
            Spacer(modifier = Modifier.height(12.dp))
            Row(
                modifier = Modifier.padding(additionalPaddingValues)
            ) {
                hintIconId?.let {
                    Icon(
                        imageVector = org.jetbrains.compose.resources.vectorResource(hintIconId),
                        contentDescription = null
                    )
                }
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = hint,
                    textAlign = TextAlign.Start,
                    style = DefaultTheme.typography.labelRegular,
                )
            }
        }

        AnimatedVisibility(visible = isOnError) {
            Spacer(modifier = Modifier.height(12.dp))
            Row(
                modifier = Modifier.padding(additionalPaddingValues)
            ) {
                errorIconId?.let {
                    Icon(
                        imageVector = org.jetbrains.compose.resources.vectorResource(errorIconId),
                        contentDescription = null
                    )
                }
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = errorText,
                    textAlign = TextAlign.Start,
                    color = DefaultTheme.colors.error,
                    style = DefaultTheme.typography.labelRegular,
                )
            }
        }
    }
}
